<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useHelp } from '@/composables/useHelp';
import HelpTOC from './HelpTOC.vue';
import HelpArticle from './HelpArticle.vue';
import HelpSearch from './HelpSearch.vue';

const {
  isOpen,
  currentArticle,
  searchQuery,
  searchResults,
  closeHelp,
  navigateTo,
  goBack,
  canGoBack,
  setSearchQuery,
  clearSearch,
} = useHelp();

const panelRef = ref<HTMLElement | null>(null);
const isSearching = computed(() => searchQuery.value.length >= 2);

// Handle escape key to close
function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen.value) {
    closeHelp();
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
});

// Close when clicking outside
function handleOverlayClick(event: MouseEvent) {
  if (event.target === event.currentTarget) {
    closeHelp();
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="help-panel">
      <div v-if="isOpen" class="help-overlay" @mousedown="handleOverlayClick">
        <div ref="panelRef" class="help-panel">
          <!-- Header -->
          <div class="help-header">
            <div class="help-header-left">
              <button
                v-if="canGoBack && !isSearching"
                class="btn btn-link btn-back"
                @click="goBack"
                title="Go back"
              >
                <i class="bi bi-arrow-left"></i>
              </button>
              <h4 class="help-title">
                <i class="bi bi-question-circle me-2"></i>
                Help
              </h4>
            </div>
            <div class="help-header-right">
              <HelpSearch
                :model-value="searchQuery"
                @update:model-value="setSearchQuery"
                @clear="clearSearch"
              />
              <button class="btn-close" @click="closeHelp" aria-label="Close"></button>
            </div>
          </div>

          <!-- Body -->
          <div class="help-body">
            <!-- Search Results -->
            <div v-if="isSearching" class="help-search-results">
              <div v-if="searchResults.length === 0" class="no-results">
                <i class="bi bi-search mb-2"></i>
                <p>No results found for "{{ searchQuery }}"</p>
                <small class="text-muted">Try different keywords or browse the table of contents</small>
              </div>
              <div v-else class="search-results-list">
                <div class="search-results-header">
                  {{ searchResults.length }} result{{ searchResults.length === 1 ? '' : 's' }}
                </div>
                <div
                  v-for="result in searchResults"
                  :key="result.id"
                  class="search-result-item"
                  @click="navigateTo(result.id)"
                >
                  <div class="result-category">{{ result.category }}</div>
                  <div class="result-title">{{ result.title }}</div>
                  <div class="result-summary">{{ result.summary }}</div>
                </div>
              </div>
            </div>

            <!-- Regular Content (TOC + Article) -->
            <template v-else>
              <!-- Table of Contents -->
              <aside class="help-toc">
                <HelpTOC @navigate="navigateTo" />
              </aside>

              <!-- Article Content -->
              <main class="help-content">
                <HelpArticle
                  v-if="currentArticle"
                  :article="currentArticle"
                  @navigate="navigateTo"
                />
                <div v-else class="no-article">
                  <i class="bi bi-book mb-2"></i>
                  <p>Select a topic from the table of contents</p>
                </div>
              </main>
            </template>
          </div>

          <!-- Footer -->
          <div class="help-footer">
            <span class="text-muted small">
              Press <kbd>F1</kbd> or <kbd>?</kbd> to toggle help
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.help-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: flex-end;
  z-index: 9999;
}

.help-panel {
  width: 1000px;
  max-width: 100%;
  height: 100%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
}

.help-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8f9fa;
  flex-shrink: 0;
}

.help-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.help-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  color: #6b7280;
  border-radius: 6px;
}

.btn-back:hover {
  background: #e5e7eb;
  color: #374151;
}

.help-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.help-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
}

.help-toc {
  width: 260px;
  flex-shrink: 0;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  background: #fafafa;
}

.help-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-width: 0;
}

.help-footer {
  padding: 10px 16px;
  border-top: 1px solid #e5e7eb;
  text-align: center;
  background: #f8f9fa;
  flex-shrink: 0;
}

.help-footer kbd {
  padding: 2px 6px;
  background: #e5e7eb;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 11px;
  font-family: inherit;
}

/* Search Results */
.help-search-results {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
  color: #6b7280;
}

.no-results i {
  font-size: 48px;
  opacity: 0.5;
}

.search-results-header {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 12px;
  margin-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.search-results-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.search-result-item {
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.search-result-item:hover {
  background: #f3f4f6;
}

.result-category {
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.result-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
}

.result-summary {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
}

.no-article {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
  color: #9ca3af;
}

.no-article i {
  font-size: 48px;
  opacity: 0.5;
}

/* Transition animations */
.help-panel-enter-active {
  animation: panel-slide-in 0.25s ease-out;
}

.help-panel-leave-active {
  animation: panel-slide-out 0.2s ease-in forwards;
}

.help-panel-enter-active .help-overlay {
  animation: overlay-fade-in 0.25s ease-out;
}

.help-panel-leave-active .help-overlay {
  animation: overlay-fade-out 0.2s ease-in forwards;
}

@keyframes panel-slide-in {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes panel-slide-out {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
}

@keyframes overlay-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes overlay-fade-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .help-panel {
    width: 100%;
  }

  .help-toc {
    display: none;
  }
}
</style>

<!-- Dark mode styles (unscoped) -->
<style>
[data-bs-theme="dark"] .help-panel {
  background: #1e293b;
}

[data-bs-theme="dark"] .help-header {
  background: #0f172a;
  border-bottom-color: #334155;
}

[data-bs-theme="dark"] .help-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .btn-back {
  color: #94a3b8;
}

[data-bs-theme="dark"] .btn-back:hover {
  background: #334155;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .help-toc {
  background: #0f172a;
  border-right-color: #334155;
}

[data-bs-theme="dark"] .help-content {
  background: #1e293b;
}

[data-bs-theme="dark"] .help-footer {
  background: #0f172a;
  border-top-color: #334155;
}

[data-bs-theme="dark"] .help-footer kbd {
  background: #334155;
  border-color: #475569;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .help-overlay {
  background: rgba(0, 0, 0, 0.6);
}

[data-bs-theme="dark"] .search-results-header {
  color: #94a3b8;
  border-bottom-color: #334155;
}

[data-bs-theme="dark"] .search-result-item:hover {
  background: #334155;
}

[data-bs-theme="dark"] .result-category {
  color: #94a3b8;
}

[data-bs-theme="dark"] .result-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .result-summary {
  color: #94a3b8;
}

[data-bs-theme="dark"] .no-results,
[data-bs-theme="dark"] .no-article {
  color: #94a3b8;
}

[data-bs-theme="dark"] .btn-close {
  filter: invert(1) grayscale(100%) brightness(200%);
}
</style>
